import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

import { useAuth } from "../context/AuthContext";

import TopNav from "../components/navigation/TopNav";
import StandardScreen, {
  ScreenContent,
} from "../components/layout/StandardScreen";
import Stack from "../components/layout/Stack";
import Inline from "../components/layout/Inline";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import FormField from "../components/forms/FormField";
import TextInput from "../components/forms/TextInput";
import SelectInput from "../components/forms/SelectInput";
import TextArea from "../components/forms/TextArea";
import {
  TableShell,
  Table,
  Th,
  Td,
  NumberTd,
} from "../components/data/Table";

import tableStyles from "../components/data/Table.module.css";

const narrowShellStyle = {
  width: "100%",
  maxWidth: "960px",
  margin: "0 auto",
};

const piecesButtonStyle = {
  minWidth: "30px",
  height: "30px",
  border: "1px solid var(--color-border)",
  borderRadius: "6px",
  background: "var(--color-surface-muted)",
  color: "var(--color-text)",
  fontSize: "var(--font-size-md)",
  fontWeight: "var(--font-weight-semibold)",
  cursor: "pointer",
};

const errorTextStyle = {
  color: "var(--color-danger)",
};

const mutedTextStyle = {
  margin: 0,
  color: "var(--color-text-muted)",
};

const disabledInputStyle = {
  width: "100%",
  minHeight: "40px",
  padding: "0 12px",
  border: "1px solid var(--color-border, #d0d7de)",
  borderRadius: "8px",
  backgroundColor: "var(--color-surface-muted, #f6f8fa)",
  color: "var(--color-text, inherit)",
  fontSize: "var(--font-size-md)",
  opacity: 1,
};

const piecesCountStyle = {
  minWidth: "1px",
  textAlign: "center",
  fontWeight: "var(--font-weight-semibold)",
  fontVariantNumeric: "tabular-nums",
};

const UpdateABill = () => {
  const { t } = useTranslation();
  const { user, loadingUser } = useAuth();

  const [excursions, setExcursions] = useState([]);
  const [selectedExcursionId, setSelectedExcursionId] = useState("");
  const [selectedExcursion, setSelectedExcursion] = useState(null);

  const [bills, setBills] = useState([]);
  const [members, setMembers] = useState([]);

  const [form, setForm] = useState(null);
  const [selectedBillId, setSelectedBillId] = useState("");

  const [payers, setPayers] = useState({});
  const [beneficiaries, setBeneficiaries] = useState({});
  const [beneficiaryMonetaryShares, setBeneficiaryMonetaryShares] = useState(
    {}
  );

  const [isLoading, setIsLoading] = useState(false);
  const [pageError, setPageError] = useState("");

  const token = localStorage.getItem("token");

  const headers = useMemo(() => {
    return { Authorization: `Bearer ${token}` };
  }, [token]);

  const payerInputRefs = useRef({});
  const definitionRef = useRef(null);

  useEffect(() => {
    const init = async () => {
      if (!token) return;

      try {
        setIsLoading(true);
        setPageError("");

        const excursionRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/excursions/i-can-see`,
          { headers }
        );

        const data = Array.isArray(excursionRes.data) ? excursionRes.data : [];
        setExcursions(data);

        if (user?.excursion?.excursion_id) {
          const activeId = String(user.excursion.excursion_id);
          const activeExc = data.find(
            (e) => String(e.excursion_id) === activeId
          );

          if (activeExc) {
            setSelectedExcursionId(activeId);
            setSelectedExcursion(activeExc);
            return;
          }
        }

        if (data.length > 0) {
          const firstExc = data[0];
          setSelectedExcursionId(String(firstExc.excursion_id));
          setSelectedExcursion(firstExc);
        }
      } catch (err) {
        console.error(err);
        setPageError(t("update_bill.errors.load_excursions"));
      } finally {
        setIsLoading(false);
      }
    };

    if (user && token) {
      init();
    }
  }, [user, token, headers, t]);

  useEffect(() => {
    const fetchBills = async () => {
      if (!selectedExcursionId || !user) return;

      try {
        setIsLoading(true);
        setPageError("");

        const billRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/bills/i-own`,
          { headers }
        );

        const uid = String(user.user_id);
        const allBills = Array.isArray(billRes.data) ? billRes.data : [];

        const filteredBills = allBills.filter((b) => {
          const isSameExcursion =
            String(b.excursion_id) === String(selectedExcursionId);

          if (!isSameExcursion) return false;

          if (user.role === "admin") {
            return true;
          }

          return String(b.user_id) === uid;
        });

        setBills(filteredBills);

        if (
          selectedBillId &&
          !filteredBills.some((b) => String(b.bill_id) === String(selectedBillId))
        ) {
          setSelectedBillId("");
          setForm(null);
          setMembers([]);
          setPayers({});
          setBeneficiaries({});
          setBeneficiaryMonetaryShares({});
        }
      } catch (err) {
        console.error(err);
        setBills([]);
        setPageError(t("update_bill.errors.load_bills"));
      } finally {
        setIsLoading(false);
      }
    };

    fetchBills();
  }, [selectedExcursionId, selectedBillId, user, headers, t]);

  useEffect(() => {
    const totalUnitShare = Object.values(beneficiaries).reduce(
      (a, b) => a + Number(b || 0),
      0
    );

    const calculated = {};

    for (const uid in beneficiaries) {
      calculated[uid] =
        totalUnitShare > 0
          ? (Number(beneficiaries[uid] || 0) / totalUnitShare) *
            (parseFloat(form?.amount) || 0)
          : 0;
    }

    setBeneficiaryMonetaryShares(calculated);
  }, [beneficiaries, form?.amount]);

  useEffect(() => {
    const loadSelectedBill = async () => {
      if (!selectedBillId) {
        setForm(null);
        setMembers([]);
        setPayers({});
        setBeneficiaries({});
        setBeneficiaryMonetaryShares({});
        return;
      }

      try {
        setIsLoading(true);
        setPageError("");

        const billRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/bills/${selectedBillId}`,
          { headers }
        );

        const bill = billRes.data;
        setForm(bill);

        const membersRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/excursions/${bill.excursion_id}/once-inside-members`,
          { headers }
        );

        const memberData = Array.isArray(membersRes.data) ? membersRes.data : [];
        setMembers(memberData);

        const nextPayers = {};
        (bill.payers || []).forEach((payer) => {
          nextPayers[String(payer.user_id)] = payer.share;
        });
        setPayers(nextPayers);

        const nextBeneficiaries = {};
        (bill.beneficiaries || []).forEach((ben) => {
          nextBeneficiaries[String(ben.user_id)] = ben.unit_share;
        });
        setBeneficiaries(nextBeneficiaries);
      } catch (err) {
        console.error(err);
        setPageError(t("update_bill.errors.load_bill_details"));
        setSelectedBillId("");
        setForm(null);
        setMembers([]);
        setPayers({});
        setBeneficiaries({});
        setBeneficiaryMonetaryShares({});
      } finally {
        setIsLoading(false);
      }
    };

    loadSelectedBill();
  }, [selectedBillId, headers, t]);

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleBeneficiaryAdjust = (userId, delta) => {
    const uid = String(userId);
    setBeneficiaries((prev) => ({
      ...prev,
      [uid]: Math.max(0, Number(prev[uid] || 0) + delta),
    }));
  };

  const selectedExcursionData = excursions.find(
    (exc) => String(exc.excursion_id) === String(selectedExcursionId)
  );

  const isExcursionLocked =
    selectedExcursionData?.status?.toLowerCase() !== "live";

  const handleSubmit = async () => {
    if (!form || isLoading) return;

    if (isExcursionLocked) {
      setPageError(t("update_bill.errors.excursion_locked"));
      return;
    }

    const amt = parseFloat(form.amount) || 0;
    const payerSum = Object.values(payers).reduce(
      (a, b) => a + parseFloat(b || 0),
      0
    );

    if (Math.abs(payerSum - amt) > 0.01) {
      alert(t("update_bill.errors.payer_sum_mismatch"));
      return;
    }

    const updated = {
      excursion_id: form.excursion_id,
      entry_date: form.entry_date,
      bill_date: form.bill_date,
      currency: form.currency,
      amount: amt,
      definition: form.definition,
      user_id: user.user_id,
      payers: Object.entries(payers).map(([uid, share]) => ({
        user_id: parseInt(uid, 10),
        share,
      })),
      beneficiaries: Object.entries(beneficiaries).map(([uid, unit]) => ({
        user_id: parseInt(uid, 10),
        unit_share: unit,
        monetary_share: beneficiaryMonetaryShares[uid] || 0,
      })),
    };

    try {
      setIsLoading(true);
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/bills/${form.bill_id}/update`,
        updated,
        { headers }
      );

      alert(
        user.role === "admin"
          ? t("update_bill.alerts.update_success_admin")
          : t("update_bill.alerts.update_success_member")
      );
    } catch (err) {
      console.error(err);
      alert(t("update_bill.alerts.update_failed"));
    } finally {
      setIsLoading(false);
    }
  };

  const focusNextPayer = (currentUid) => {
    const ordered = members.map((m) => String(m.user_id));
    const idx = ordered.indexOf(String(currentUid));

    if (idx >= 0 && idx < ordered.length - 1) {
      const nextUid = ordered[idx + 1];
      payerInputRefs.current[nextUid]?.focus?.();
      return;
    }

    if (definitionRef.current?.focus) {
      definitionRef.current.focus();
      return;
    }

    payerInputRefs.current[String(currentUid)]?.blur?.();
  };

  const payerSum = Object.values(payers).reduce(
    (a, b) => a + parseFloat(b || 0),
    0
  );

  const amtForMsg = parseFloat(form?.amount) || 0;

  const payerMsg =
    Math.abs(payerSum - amtForMsg) < 0.01
      ? t("update_bill.validation.ok")
      : t("update_bill.validation.not_ok");

  const selectedExcursionLabel = selectedExcursionData
    ? `${selectedExcursionData.excursion_id} - ${selectedExcursionData.short_name}`
    : isLoading
    ? t("update_bill.messages.loading_excursions")
    : t("update_bill.labels.no_excursion");

  const isEligible = user?.role === "admin" || user?.role === "member";

  if (loadingUser) {
    return (
      <StandardScreen
        topNav={<TopNav title={t("update_bill.title")} mode="main" />}
        isLoading
      />
    );
  }

  if (!user) {
    return (
      <StandardScreen
        topNav={<TopNav title={t("update_bill.title")} mode="main" />}
        isError
        errorState={
          <div style={errorTextStyle}>
            {t("update_bill.errors.no_session")}
          </div>
        }
      />
    );
  }

  if (!token || !isEligible) {
    return (
      <StandardScreen topNav={<TopNav title={t("update_bill.title")} mode="main" />}>
        <ScreenContent>
          <Card variant="accent">
            <strong>{t("update_bill.errors.login_required")}</strong>
          </Card>
        </ScreenContent>
      </StandardScreen>
    );
  }

  return (
    <StandardScreen
      topNav={<TopNav title={t("update_bill.title")} mode="main" />}
      bottomAction={
        <Button
          fullWidth
          size="lg"
          onClick={handleSubmit}
          disabled={!form || isLoading || isExcursionLocked}
        >
          {isLoading
            ? t("update_bill.actions.updating")
            : t("update_bill.actions.update")}
        </Button>
      }
    >
      <ScreenContent>
        <div style={narrowShellStyle}>
          <Stack gap="5">
            <Card>
              <Stack gap="4">
                <FormField label={t("update_bill.fields.excursion")}>
                  <input
                    type="text"
                    value={selectedExcursionLabel}
                    disabled
                    readOnly
                    style={disabledInputStyle}
                  />
                </FormField>

                <FormField label={t("update_bill.fields.bill")}>
                  <SelectInput
                    value={selectedBillId}
                    onChange={(e) => setSelectedBillId(e.target.value)}
                    disabled={
                      !selectedExcursionId ||
                      bills.length === 0 ||
                      isLoading ||
                      isExcursionLocked
                    }
                  >
                    <option value="">
                      {t("update_bill.placeholders.select_bill")}
                    </option>
                    {bills.map((b) => (
                      <option key={b.bill_id} value={String(b.bill_id)}>
                        {`${b.bill_id} | ${b.amount} ${b.currency} | ${b.definition}${
                          String(b.user_id) === String(user.user_id)
                            ? t("update_bill.labels.you_own")
                            : user.role === "admin"
                            ? ` (${t("update_bill.labels.owner")}: ${b.user_id})`
                            : ""
                        }`}
                      </option>
                    ))}
                  </SelectInput>
                </FormField>

                {pageError ? (
                  <div style={errorTextStyle}>{pageError}</div>
                ) : null}

                {!isLoading &&
                selectedExcursionId &&
                bills.length === 0 &&
                !pageError ? (
                  <p style={mutedTextStyle}>
                    {user.role === "admin"
                      ? t("update_bill.messages.no_bills_admin")
                      : t("update_bill.messages.no_bills_user")}
                  </p>
                ) : null}

                {isExcursionLocked ? (
                  <div style={errorTextStyle}>
                    {t("update_bill.errors.excursion_locked")}
                  </div>
                ) : null}
              </Stack>
            </Card>

            {form ? (
              <>
                <Card
                  title={t("update_bill.sections.details")}
                  subtitle={t("update_bill.sections.details_sub")}
                >
                  <Stack gap="4">
                    <Inline gap="4" wrap>
                      <div style={{ flex: "1 1 220px" }}>
                        <FormField label={t("update_bill.fields.entry_date")}>
                          <TextInput
                            type="date"
                            value={form.entry_date || ""}
                            onChange={(e) =>
                              handleFieldChange("entry_date", e.target.value)
                            }
                            disabled={isLoading || isExcursionLocked}
                          />
                        </FormField>
                      </div>

                      <div style={{ flex: "1 1 220px" }}>
                        <FormField label={t("update_bill.fields.bill_date")}>
                          <TextInput
                            type="date"
                            value={form.bill_date || ""}
                            onChange={(e) =>
                              handleFieldChange("bill_date", e.target.value)
                            }
                            disabled={isLoading || isExcursionLocked}
                          />
                        </FormField>
                      </div>
                    </Inline>

                    <Inline gap="4" wrap>
                      <div style={{ flex: "1 1 220px" }}>
                        <FormField label={t("update_bill.fields.amount")}>
                          <TextInput
                            type="number"
                            inputMode="decimal"
                            step="0.01"
                            value={form.amount ?? ""}
                            onChange={(e) =>
                              handleFieldChange("amount", e.target.value)
                            }
                            disabled={isLoading || isExcursionLocked}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                const first = members?.[0]?.user_id;
                                if (first != null) {
                                  payerInputRefs.current[String(first)]?.focus?.();
                                } else {
                                  definitionRef.current?.focus?.();
                                }
                              }
                            }}
                          />
                        </FormField>
                      </div>

                      <div style={{ flex: "1 1 220px" }}>
                        <FormField label={t("update_bill.fields.currency")}>
                          <SelectInput
                            value={form.currency || "TRL"}
                            onChange={(e) =>
                              handleFieldChange("currency", e.target.value)
                            }
                            disabled={isLoading || isExcursionLocked}
                          >
                            <option value="TRL">TRL</option>
                            <option value="USD">USD</option>
                            <option value="EUR">EUR</option>
                          </SelectInput>
                        </FormField>
                      </div>
                    </Inline>

                    <FormField label={t("update_bill.fields.definition")}>
                      <TextArea
                        ref={definitionRef}
                        value={form.definition || ""}
                        onChange={(e) =>
                          handleFieldChange("definition", e.target.value)
                        }
                        rows={4}
                        disabled={isLoading || isExcursionLocked}
                      />
                    </FormField>
                  </Stack>
                </Card>

                {members.length > 0 ? (
                  <Card
                    title={t("update_bill.sections.participants")}
                    subtitle={t("update_bill.sections.participants_sub")}
                  >

                  <TableShell>
                    <Table>
                      <thead>
                        <tr>
                          <Th columnIndex={0} sticky className={tableStyles.col1}>
                            {t("update_bill.table.member")}
                          </Th>

                          <Th
                            align="right"
                            className={`${tableStyles.col2} ${tableStyles.tightPadding}`}
                          >
                            {t("update_bill.table.paid")}
                          </Th>

                          <Th
                            className={`${tableStyles.col3} ${tableStyles.tightPadding}`}
                          >
                            {t("update_bill.table.share")}
                          </Th>

                          <Th align="right">
                            {t("update_bill.table.equals")}
                          </Th>
                        </tr>
                      </thead>

                      <tbody>
                        {members.map((m) => {
                          const uid = String(m.user_id);
                          const unit = beneficiaries[uid] || 0;

                          const totalUnit = Object.values(beneficiaries).reduce(
                            (a, b) => a + Number(b || 0),
                            0
                          );

                          const monetary =
                            totalUnit > 0
                              ? (
                                  (unit / totalUnit) *
                                  (parseFloat(form.amount) || 0)
                                ).toFixed(2)
                              : "0.00";

                          return (
                            <tr key={m.user_id}>
                              <Td
                                columnIndex={0}
                                sticky
                                className={tableStyles.col1}
                                title={`${m.name} ${m.surname}`}
                              >
                                <span className={tableStyles.cellInner}>
                                  {m.name} {m.surname}
                                </span>
                              </Td>

                              <NumberTd
                                className={`${tableStyles.col2} ${tableStyles.tightPadding}`}
                              >
                                <TextInput
                                  ref={(el) => {
                                    payerInputRefs.current[uid] = el;
                                  }}
                                  type="number"
                                  inputMode="decimal"
                                  step="0.01"
                                  enterKeyHint="next"
                                  value={payers[uid] ?? 0}
                                  onChange={(e) =>
                                    setPayers((prev) => ({
                                      ...prev,
                                      [uid]: parseFloat(e.target.value) || 0,
                                    }))
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      e.preventDefault();
                                      focusNextPayer(uid);
                                    }
                                  }}
                                  disabled={isLoading || isExcursionLocked}
                                />
                              </NumberTd>

                              <Td
                                className={`${tableStyles.col3} ${tableStyles.tightPadding}`}
                              >
                                <Inline gap="1" align="center">
                                  <button
                                    type="button"
                                    style={piecesButtonStyle}
                                    disabled={isLoading || isExcursionLocked}
                                    onClick={() =>
                                      handleBeneficiaryAdjust(uid, -1)
                                    }
                                  >
                                    -
                                  </button>

                                  <span style={piecesCountStyle}>{unit}</span>

                                  <button
                                    type="button"
                                    style={piecesButtonStyle}
                                    disabled={isLoading || isExcursionLocked}
                                    onClick={() =>
                                      handleBeneficiaryAdjust(uid, 1)
                                    }
                                  >
                                    +
                                  </button>
                                </Inline>
                              </Td>

                              <NumberTd>{monetary}</NumberTd>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </TableShell>                  
                  
                  
                  </Card>
                ) : null}

                <Card title={t("update_bill.sections.validation")}>
                  <strong>{payerMsg}</strong>
                </Card>
              </>
            ) : null}
          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
};

export default UpdateABill;